// Step 8.1.2 hotfix: pure helpers for reading the Cognito group claim
// out of an access token. Mirrors the decode logic in middleware.ts so
// the client-side useIsAdmin hook + the edge middleware stay in lockstep.
// Token signature is NOT verified here - the server-side requireAdmin
// helper (lib/auth/requireAdmin.ts) is the cryptographic gate; this
// helper exists only to drive UI conditional rendering.

const ADMIN_GROUP = "admin";
const APPROVED_GROUP = "approved";

// PASTE #246: price + authenticated-nav visibility keys on the `approved`
// (or `admin`) group, NOT mere sign-in. A signed-in-but-not-approved
// user - most importantly a fresh federated login - carries neither, so
// they see zero prices and no portal nav. Client-side (base64) helper for
// UX gating; the authoritative gate is the crypto-verified server /
// /api layer.
export function isApprovedOrAdminGroups(groups: string[]): boolean {
  return groups.includes(APPROVED_GROUP) || groups.includes(ADMIN_GROUP);
}

export function canSeePricesFromAccessToken(
  token: string | null | undefined,
): boolean {
  return isApprovedOrAdminGroups(groupsFromAccessToken(token));
}

export function groupsFromAccessToken(token: string | null | undefined): string[] {
  if (!token) return [];
  const parts = token.split(".");
  if (parts.length !== 3) return [];
  try {
    const payloadB64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = payloadB64 + "==".slice(0, (4 - (payloadB64.length % 4)) % 4);
    const payloadStr =
      typeof atob === "function"
        ? atob(padded)
        : Buffer.from(payloadB64, "base64").toString("utf8");
    const payload = JSON.parse(payloadStr) as {
      "cognito:groups"?: string[];
      exp?: number;
    };
    if (payload.exp && payload.exp * 1000 < Date.now()) return [];
    return payload["cognito:groups"] ?? [];
  } catch {
    return [];
  }
}

export function isAdminFromAccessToken(
  token: string | null | undefined,
): boolean {
  return groupsFromAccessToken(token).includes(ADMIN_GROUP);
}
